import React from "react";
import { Navigate } from "react-router";
import { useApiQuery } from "jadro-pico-app/Api/useApiQuery";
import AppCard from "jadro-pico-app/Components/UI/AppCard/AppCard";
import AppTileGrid from "jadro-pico-app/Components/UI/AppCard/AppTileGrid";
import AppTileGridSkeleton from "jadro-pico-app/Components/UI/AppCard/AppTileGridSkeleton";
import { picoAppIconMap } from "jadro-pico-app/Components/UI/AppCard/picoAppIconMap";
import { picoAppLogoMap } from "jadro-pico-app/Components/UI/AppCard/picoAppLogoMap";
import PageLayout from "jadro-pico-app/Components/UI/Layouts/PageLayout/PageLayout";
import FontText from "jadro-pico-app/Components/UI/FontText/FontText";
import { FontSizeEnum, FontTypeEnum } from "jadro-pico-app/Components/UI/FontText/FontTextEnums";
import { ThemeColorEnum } from "jadro-pico-app/Components/UI/Themes/ThemeEnums";
import { PicoAppDto } from "jadro-pico/apps/PicoAppDto";
import { useResource } from "jadro-pico-app/Resources/useResource";
import { useRoutePaths } from "jadro-pico-app/Routes/useRoutePaths";
import { PicoComRoutes } from "jadro-pico-app/Routes/PicoComRoutes";
import { DashboardResources } from "../../Resources/LoginResources";
import { QueryKeys } from "../../queryKeys";

interface UserProfile {
  sub: string;
  preferred_username?: string;
  resource_access?: Record<string, { roles: string[] }>;
}

const Dashboard: React.FC = () => {
  const t = useResource(DashboardResources);
  const paths = useRoutePaths(PicoComRoutes);

  const {
    data: user,
    isLoading: userLoading,
    error: userError,
  } = useApiQuery<UserProfile>({
    url: `${import.meta.env.VITE_PICO_API_URL}/auth/user`,
    queryKey: QueryKeys.me,
  });
  const { data: apps, isLoading: appsLoading } = useApiQuery<PicoAppDto[]>({
    url: `${import.meta.env.VITE_PICO_API_URL}/apps`,
    queryKey: QueryKeys.appsCatalog,
    staleTime: Infinity,
  });

  if (userError || (!userLoading && !user)) return <Navigate to={paths.LOGIN} replace />;

  const isLoading = userLoading || appsLoading || !apps;
  const homepageApps = apps?.filter((app) => app.showInLauncher) ?? [];

  return (
    <PageLayout>
      <FontText
        text={t.title}
        $color={ThemeColorEnum.ON_SURFACE}
        $fontType={FontTypeEnum.TITLE}
        $fontSize={FontSizeEnum.LARGE}
      />
      {isLoading ? (
        <AppTileGridSkeleton />
      ) : (
        <AppTileGrid>
          {homepageApps.map((app) => (
            <AppCard
              key={app.id}
              name={app.name}
              href={app.feUrl}
              icon={picoAppIconMap[app.id]}
              logo={picoAppLogoMap[app.id]}
              variant="tile"
            />
          ))}
        </AppTileGrid>
      )}
    </PageLayout>
  );
};

export default Dashboard;
